/**
 * parad-cart-estimate.js
 * Estimation des frais de livraison + total estimé au panier (Action 12).
 *
 * Implémentation UNIQUE, partagée par les deux emplacements :
 *   - snippets/cart-drawer.liquid  (tiroir panier, rendu par sections/header.liquid
 *     sur TOUTES les pages, donc aussi sur /cart)
 *   - sections/main-cart.liquid    (page /cart)
 * Le markup et les valeurs de réglage viennent de snippets/cart-shipping-estimate.liquid.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI CE FICHIER EXISTE (ce qu'il remplace)
 * ─────────────────────────────────────────────────────────────────────────────
 * L'ancien code était dupliqué à l'identique dans les deux fichiers Liquid et
 * souffrait de quatre défauts, tous corrigés ici :
 *
 * 1. Rafraîchissement à l'aveugle : `document.addEventListener('click', () =>
 *    setTimeout(fetch('/cart.js'), 800))`. Le montant était relu 800 ms après
 *    N'IMPORTE QUEL clic de la page. Si la mise à jour AJAX du panier dépassait
 *    800 ms (mobile, 4G), /cart.js renvoyait l'ANCIEN panier : le sous-total du
 *    thème était à jour et le « Total estimé » non — deux chiffres d'argent
 *    contradictoires côte à côte. On ne parie plus sur une durée : on écoute le
 *    fait accompli (voir « SIGNAUX » plus bas).
 *
 * 2. Erreur silencieuse : aucun `.catch`. Ici il n'y a plus AUCUN appel réseau,
 *    donc plus de promesse à rejeter (voir défaut 2 bis ci-dessous).
 *
 * 2 bis. Plus de fetch du tout. L'information est déjà dans la page : le thème
 *    vient de la télécharger pour son propre sous-total. La relire au lieu de la
 *    redemander supprime d'un coup la course, le doublon d'appel et le risque
 *    réseau. Corollaire : si le réseau tombe, le thème n'actualise pas son
 *    sous-total et nous n'actualisons pas le nôtre — les deux montants restent
 *    cohérents, ce qui vaut mieux qu'un total faux.
 *
 * 3. Double exécution : sur /cart les DEUX scripts tournaient (deux écouteurs de
 *    clic sur `document`, deux fetch par clic). Ici, un garde d'idempotence
 *    (window.paradCartEstimate) et UN SEUL écouteur `document`, quelle que soit
 *    la page et quel que soit le nombre de blocs présents (0, 1 ou 2).
 *
 * 4. Valeurs codées en dur en quatre endroits : elles sont désormais définies
 *    une seule fois dans snippets/cart-shipping-estimate.liquid et transmises
 *    ici par les attributs data-* du bloc. Voir ce fichier pour les changer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SIGNAUX DE MISE À JOUR (références : assets/theme.js du thème Impulse 9.1)
 * ─────────────────────────────────────────────────────────────────────────────
 * Signal A — événement `cart:updated`, émis par theme.js l. 1883 dans
 *   CartForm.quantityChanged, juste après la réponse de /cart/change.js.
 *   `evt.detail.cart` EST la réponse Shopify : montants en centimes, autorité
 *   absolue. Indispensable sur /cart, car un changement de quantité dans le
 *   formulaire de page (namespace `.cart-page`) ne reconstruit PAS le tiroir
 *   (theme.js l. 1710 : chaque CartForm n'écoute que son propre namespace),
 *   alors que `cart:updated` est diffusé sur `document`.
 *
 * Signal B — MutationObserver sur `[data-products]`. theme.js l. 1803-1804
 *   (CartForm.cartMarkup) vide ce nœud puis y injecte le `.cart__items`
 *   fraîchement téléchargé (templates/cart.ajax.liquid l. 13-15), porteur de
 *   `data-cart-subtotal` (centimes) et `data-count`. C'est le FAIT ACCOMPLI :
 *   la mutation ne peut pas survenir avant que le thème n'ait les bons chiffres.
 *   Mieux : `data-cart-subtotal` est EXACTEMENT la valeur que le thème écrit au
 *   même instant dans `[data-subtotal]` (l. 1788 puis l. 1807) — nos deux
 *   montants ne peuvent donc plus se contredire, par construction.
 *   Signal B est indispensable car l'ajout au panier appelle `buildCart()` SANS
 *   émettre `cart:updated` (theme.js l. 4515-4518, CartDrawer.init) : le
 *   signal A seul manquerait tous les ajouts produit.
 *
 * Les deux signaux écrivent le même état, chacun recalculant tout à partir d'un
 * sous-total en centimes : l'opération est idempotente et insensible à l'ordre.
 * On ne filtre volontairement PAS les valeurs « plus anciennes » : le but est de
 * refléter ce que le thème affiche, pas de le corriger.
 */
(function () {
  'use strict';

  // Le snippet étant rendu deux fois sur /cart (tiroir global + section
  // main-cart), cette balise <script> peut apparaître deux fois dans la page.
  // Le navigateur ne télécharge le fichier qu'une fois mais l'exécute deux fois :
  // ce garde assure qu'il n'y aura jamais qu'un seul jeu d'écouteurs.
  if (window.paradCartEstimate) {
    return;
  }

  var SELECTORS = {
    block: '[data-parad-cart-estimate]',
    products: '[data-products]',
    items: '.cart__items',
    shipping: '[data-parad-shipping]',
    total: '[data-parad-total]',
    message: '[data-parad-message]',
    progress: '[data-parad-progress]'
  };

  // Libellés. Ils doivent rester le MOT POUR MOT de ce que rend le Liquid dans
  // snippets/cart-shipping-estimate.liquid : le premier affichage vient de
  // Liquid, celui-ci le remplace au premier changement de quantité. Toute
  // divergence se voit comme un texte qui change tout seul — c'est ce qui est
  // arrivé avec « livraison gratuite ! » ici contre « livraison offerte » côté
  // Liquid. Phrase volontairement courte : au-delà, elle passe sur deux lignes
  // dans le tiroir et mange la place des articles.
  var LABELS = {
    free: 'Offerte',
    from: 'à partir de ',
    freeReached: 'Livraison offerte 🎉',
    remainingPrefix: 'Plus que ',
    remainingSuffix: ' pour la livraison offerte'
  };

  /**
   * Formatage monétaire.
   * On réutilise la fonction du thème (theme.js l. 278-345) avec le même format
   * que le sous-total (theme.js l. 1807) : theme.settings.moneyFormat vaut
   * shop.money_format (layout/theme.liquid l. 317), c'est-à-dire exactement ce
   * que produit le filtre Liquid `money`. L'ancien code utilisait
   * toLocaleString('fr-FR'), qui insère une espace fine insécable avant l'euro
   * là où Shopify met une espace ordinaire : deux écritures différentes pour la
   * même monnaie, sur la même ligne que des montants rendus en Liquid.
   * Le repli toLocaleString ne sert que si theme.js n'a pas été évalué.
   *
   * Note sécurité : la chaîne retournée n'est jamais dérivée d'une saisie
   * cliente. Elle combine un entier (centimes, passé par parseInt) et
   * shop.money_format, réglage d'administration. Les seuls textes qui
   * l'entourent sont les littéraux de LABELS. Aucun chemin d'injection depuis
   * le navigateur : c'est ce qui autorise l'innerHTML plus bas.
   */
  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
      return window.theme.Currency.formatMoney(
        cents,
        window.theme.settings && window.theme.settings.moneyFormat
      );
    }
    return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  }

  /**
   * Panier vide : on masque le bloc entier. Sans cela on annoncerait
   * « Total estimé : à partir de 3,90 € » sur un panier à zéro article.
   * `itemCount` est prioritaire sur le sous-total, car un sous-total nul avec
   * des articles reste possible (remise de 100 %, article offert).
   */
  function isEmptyCart(subtotal, itemCount) {
    if (typeof itemCount === 'number' && !isNaN(itemCount)) {
      return itemCount <= 0;
    }
    return subtotal <= 0;
  }

  /**
   * Écrit l'estimation dans TOUS les blocs présents dans la page.
   * `subtotal` et `itemCount` sont en centimes / en unités, jamais formatés.
   * Si le sous-total n'est pas exploitable, on ne touche à RIEN : mieux vaut
   * laisser le montant rendu par Liquid qu'afficher un chiffre faux.
   */
  function render(subtotal, itemCount) {
    if (typeof subtotal !== 'number' || isNaN(subtotal) || subtotal < 0) {
      return;
    }

    var empty = isEmptyCart(subtotal, itemCount);

    document.querySelectorAll(SELECTORS.block).forEach(function (block) {
      // Seuil et tarif mini viennent du snippet Liquid : une seule définition
      // pour le rendu serveur et pour ce script.
      var seuil = parseInt(block.getAttribute('data-seuil'), 10);
      var mini = parseInt(block.getAttribute('data-mini'), 10);

      // Configuration illisible → on laisse le rendu Liquid intact.
      if (isNaN(seuil) || isNaN(mini)) {
        return;
      }

      // Attribut `hidden` (feuille de style du navigateur) + classe `hide` du
      // thème (display:none !important, theme.css.liquid l. 2865) : la seconde
      // résiste à une CSS d'application tierce qui redéfinirait `display`.
      block.hidden = empty;
      if (block.classList) {
        block.classList.toggle('hide', empty);
      }
      if (empty) {
        return;
      }

      var offerte = subtotal >= seuil;

      var shippingEl = block.querySelector(SELECTORS.shipping);
      if (shippingEl) {
        // innerHTML et non innerText : formatMoney peut produire un <sup>
        // quand le réglage superScriptPrice est actif (theme.js l. 312-314),
        // et c'est ce que fait le thème pour le sous-total (l. 1807).
        shippingEl.innerHTML = offerte ? LABELS.free : LABELS.from + formatMoney(mini);
      }

      var totalEl = block.querySelector(SELECTORS.total);
      if (totalEl) {
        var total = subtotal + (offerte ? 0 : mini);
        totalEl.innerHTML = offerte ? formatMoney(total) : LABELS.from + formatMoney(total);
      }

      var messageEl = block.querySelector(SELECTORS.message);
      if (messageEl) {
        messageEl.innerHTML = offerte
          ? LABELS.freeReached
          : LABELS.remainingPrefix + formatMoney(seuil - subtotal) + LABELS.remainingSuffix;
      }

      // Barre de progression (action 12). Math.floor et non Math.round : le
      // Liquid utilise `divided_by` sur des entiers, donc une division
      // entière. Les deux rendus doivent donner exactement la même largeur,
      // sinon la barre saute d'un pixel au premier changement de quantité.
      var progressEl = block.querySelector(SELECTORS.progress);
      if (progressEl) {
        var pct = Math.floor((subtotal * 100) / seuil);
        if (pct > 100) { pct = 100; }
        if (pct < 0) { pct = 0; }
        progressEl.style.width = pct + '%';
      }
    });
  }

  /**
   * Signal B : lit le sous-total dans le markup que le thème vient d'injecter.
   * Tant que `.cart__items` est absent (nœud vidé, ou page /cart au premier
   * rendu où le Liquid pose les cart-item sans conteneur), on ne fait rien.
   */
  function readFromProducts(productsNode) {
    if (!productsNode || !productsNode.querySelector) {
      return;
    }

    var items = productsNode.querySelector(SELECTORS.items);
    if (!items) {
      return;
    }

    var subtotal = parseInt(items.getAttribute('data-cart-subtotal'), 10);
    var count = parseInt(items.getAttribute('data-count'), 10);

    if (isNaN(subtotal)) {
      return; // markup inattendu : on n'invente pas de montant
    }

    render(subtotal, isNaN(count) ? undefined : count);
  }

  // Un seul MutationObserver, quel que soit le nombre de [data-products]
  // (un observer peut surveiller plusieurs cibles).
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      readFromProducts(mutations[i].target);
    }
  });

  function observeProducts() {
    document.querySelectorAll(SELECTORS.products).forEach(function (node) {
      if (node.paradEstimateObserved) {
        return;
      }
      node.paradEstimateObserved = true;
      observer.observe(node, { childList: true });
    });
  }

  function init() {
    observeProducts();

    // Signal A.
    document.addEventListener('cart:updated', function (evt) {
      var cart = evt && evt.detail && evt.detail.cart;
      if (!cart) {
        return;
      }
      render(cart.total_price, cart.item_count);
    });

    // L'éditeur de thème remplace le nœud d'une section rechargée : le
    // [data-products] observé disparaît. On re-scanne pour ne pas devenir sourd.
    document.addEventListener('shopify:section:load', observeProducts);

    // Aucun rendu initial : les montants sont déjà justes, posés par Liquid au
    // rendu serveur. C'est aussi ce qui supprime le clignotement du message,
    // auparavant vide jusqu'au retour du fetch.
  }

  // Le tiroir panier appelle buildCart() dès son initialisation
  // (theme.js l. 4513, sur DOMContentLoaded) : les observers doivent être en
  // place avant. Ce script étant chargé en `defer`, il s'exécute après l'analyse
  // du document et avant DOMContentLoaded — la branche `else` est donc la voie
  // normale, la première n'est qu'une sécurité.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sert de garde d'idempotence ET de poignée de débogage en console.
  window.paradCartEstimate = {
    render: render,
    // paradCartEstimate.refresh() : relit le DOM et réaffiche.
    refresh: function () {
      document.querySelectorAll(SELECTORS.products).forEach(readFromProducts);
    }
  };
})();
